import { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Users, Search, Mail, Phone, Calendar,
  CheckCircle, XCircle, Trash2, Loader2, Download,
  ArrowLeft, MailCheck, MessageCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';

export default function Inscritos() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data: inscricoes, isLoading } = useQuery({
    queryKey: ['webinar-inscricoes'],
    queryFn: () => base44.entities.WebinarInscricao.list('-created_date'),
    initialData: []
  });

  const filteredInscricoes = inscricoes.filter((i: any) => 
    i.nome_completo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.telefone?.includes(searchTerm)
  );

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    await base44.entities.WebinarInscricao.delete(deleteId);
    queryClient.invalidateQueries({ queryKey: ['webinar-inscricoes'] });
    setDeleteId(null);
    setDeleting(false);
  };

  const exportToCSV = () => {
    const headers = ['Nome', 'Email', 'Telefone', 'CPF', 'Data de Inscrição', 'Email Enviado'];
    const rows = inscricoes.map((i: any) => [
      i.nome_completo,
      i.email,
      i.telefone,
      i.cpf || '-',
      format(new Date(i.created_date), 'dd/MM/yyyy HH:mm'),
      i.email_enviado ? 'Sim' : 'Não'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `inscritos-webinar-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <Link to={createPageUrl('ConfiguracaoWebinar')}>
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <Users className="w-8 h-8 text-pink-500" />
                  Inscritos no Webinar
                </h1>
                <p className="text-gray-500 mt-1">
                  {inscricoes.length} {inscricoes.length === 1 ? 'pessoa inscrita' : 'pessoas inscritas'}
                </p>
              </div>
            </div>
            <Button 
              onClick={exportToCSV}
              variant="outline"
              className="flex items-center gap-2"
              disabled={inscricoes.length === 0}
            >
              <Download className="w-4 h-4" />
              Exportar CSV
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-gray-900">{inscricoes.length}</p>
                  <p className="text-sm text-gray-500">Total de Inscritos</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <MailCheck className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-gray-900">
                    {inscricoes.filter((i: any) => i.email_enviado).length}
                  </p>
                  <p className="text-sm text-gray-500">Emails Enviados</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Calendar className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-gray-900">
                    {inscricoes.filter((i: any) => {
                      const today = new Date();
                      const created = new Date(i.created_date);
                      return created.toDateString() === today.toDateString();
                    }).length}
                  </p>
                  <p className="text-sm text-gray-500">Hoje</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Phone className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-gray-900">
                    {inscricoes.filter((i: any) => i.telefone).length}
                  </p>
                  <p className="text-sm text-gray-500">Com WhatsApp</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Buscar por nome, email ou telefone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
                </div>
              ) : filteredInscricoes.length === 0 ? (
                <div className="text-center py-20">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {searchTerm ? 'Nenhum inscrito encontrado' : 'Nenhum inscrito ainda'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>CPF</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <AnimatePresence>
                        {filteredInscricoes.map((inscrito: any, index: number) => (
                          <motion.tr
                            key={inscrito.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ delay: index * 0.05 }}
                            className="group"
                          >
                            <TableCell className="font-medium">
                              {inscrito.nome_completo}
                            </TableCell>
                            <TableCell>
                              <a 
                                href={`mailto:${inscrito.email}`}
                                className="text-blue-600 hover:underline flex items-center gap-1"
                              >
                                <Mail className="w-3 h-3" />
                                {inscrito.email}
                              </a>
                            </TableCell>
                            <TableCell>
                              <a 
                                href={`https://wa.me/55${inscrito.telefone?.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:underline flex items-center gap-1"
                              >
                                <MessageCircle className="w-3 h-3" />
                                {inscrito.telefone}
                              </a>
                            </TableCell>
                            <TableCell className="text-gray-500">
                              {inscrito.cpf || '-'}
                            </TableCell>
                            <TableCell className="text-gray-500 text-sm">
                              {format(new Date(inscrito.created_date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                            </TableCell>
                            <TableCell>
                              {inscrito.email_enviado ? (
                                <Badge className="bg-green-100 text-green-700 border-0">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Email enviado
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 border-0">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Pendente
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteId(inscrito.id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </motion.tr>
                        ))}
                      </AnimatePresence>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Delete Dialog */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover este inscrito? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

